import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true
});

let isRefreshing = false;
let failedQueue = [];
let lastRefreshTime = 0;
const REFRESH_THRESHOLD = 25 * 60 * 1000; 

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  window.dispatchEvent(new CustomEvent("session:expired"));
};

const refreshToken = async () => {
  try {
    const { data: { token, user } } = await api.post("/auth/refresh_token");
    localStorage.setItem("token", JSON.stringify(token));
    api.defaults.headers.Authorization = `Bearer ${token}`;
    lastRefreshTime = Date.now();
    return { token, user };
  } catch (err) {
    if (err?.response?.data?.error === "ERR_USER_INACTIVE") {
      throw err;
    }
    handleLogout();
    throw err;
  }
};

let userInactive = false;

const setUserInactiveFlag = (value) => {
  userInactive = value;
};

api.interceptors.request.use(
  async config => {
    if (userInactive && !config.url.includes("/auth/logout")) {
      return Promise.reject(new Error("User is inactive"));
    }

    if (
      config.url.includes("/auth/refresh_token") ||
      config.url.includes("/auth/logout") ||
      config.url.includes("/auth/login") ||
      config.url.includes("/forgot-password") ||
      config.url.includes("/reset-password")
    ) {
      // Rotas de entrada de auth NÃO devem carregar token nem disparar refresh
      // (senão o login entra na tempestade de refresh e volta 401). Fix 28/06.
      return config;
    }

    const token = localStorage.getItem("token");
    if (token) {
      const now = Date.now();
      if (now - lastRefreshTime > REFRESH_THRESHOLD) {
        // SINGLE-FLIGHT: sem isto, todas as chamadas concorrentes no load disparam
        // refresh_token ao mesmo tempo (lastRefreshTime=0) -> tempestade de 10+/seg
        // que rotacionava o token, derrubava a sessão e dava 401 no login. Fix 28/06.
        if (isRefreshing) {
          try {
            const queuedToken = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            config.headers.Authorization = `Bearer ${queuedToken}`;
          } catch (err) {
            return Promise.reject(err);
          }
        } else {
          isRefreshing = true;
          try {
            const { token: newToken } = await refreshToken();
            processQueue(null, newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (err) {
            processQueue(err, null);
            if (err?.response?.data?.error === "ERR_USER_INACTIVE") {
              setUserInactiveFlag(true);
            }
            console.error("Error refreshing token:", err);
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        }
      } else {
        try {
          const parsedToken = JSON.parse(token);
          config.headers.Authorization = `Bearer ${parsedToken}`;
        } catch (err) {
          console.error("Erro ao processar token:", err);
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error?.response?.data?.error === "ERR_SESSION_EXPIRED" || error?.response?.data?.error === "ERR_USER_INACTIVE") {
      if (error?.response?.data?.error === "ERR_USER_INACTIVE") {
        return Promise.reject(error);
      }

      if (!originalRequest?.url?.includes("/auth/logout")) {
        handleLogout();
      }
      return Promise.reject(error);
    }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { token } = await refreshToken();
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const markUserAsInactive = () => {
  setUserInactiveFlag(true);
};

export default api;